import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [forgot, setForgot] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const sendReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email, origin: window.location.origin });
      toast.success("Se l'email esiste, riceverai le istruzioni per il reset.");
      setForgot(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="w-full max-w-sm">
        <div className="flex items-center gap-2 mb-8 justify-center">
          <img src="/logo-lovac.png" alt="Lovac" className="h-9 w-auto object-contain" />
          <span className="text-base font-medium text-faint tracking-tight">Leadgen</span>
        </div>

        <div className="bg-white border border-line rounded-[8px] p-6">
          <h1 className="text-2xl font-semibold tracking-tight text-ink mb-1">
            {forgot ? "Recupera password" : "Accedi"}
          </h1>
          <p className="text-xs text-subtle mb-6">
            {forgot ? "Ti invieremo un link per reimpostarla." : "Piattaforma interna di lead generation."}
          </p>

          <form onSubmit={forgot ? sendReset : submit} className="flex flex-col gap-4">
            <div>
              <label className="text-sm font-semibold text-ink block mb-1.5">Email</label>
              <input
                data-testid="login-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-white border border-line rounded-[6px] px-3 py-2 text-sm placeholder-faint focus:outline-none focus:ring-2 focus:ring-brand transition-colors"
                placeholder="nome@lovac.it"
              />
            </div>
            {!forgot && (
              <div>
                <label className="text-sm font-semibold text-ink block mb-1.5">Password</label>
                <input
                  data-testid="login-password"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-white border border-line rounded-[6px] px-3 py-2 text-sm placeholder-faint focus:outline-none focus:ring-2 focus:ring-brand transition-colors"
                  placeholder="••••••••"
                />
              </div>
            )}

            {error && <p data-testid="login-error" className="text-sm text-destructive">{error}</p>}

            <button
              data-testid={forgot ? "send-reset-button" : "login-submit"}
              type="submit"
              disabled={loading}
              className="bg-brand text-ink font-medium rounded-[6px] px-4 py-2 hover:bg-brand-hover disabled:opacity-50 transition-colors"
            >
              {loading ? "Attendere…" : forgot ? "Invia link" : "Accedi"}
            </button>
          </form>

          <button
            data-testid="toggle-forgot"
            onClick={() => { setForgot(!forgot); setError(""); }}
            className="text-xs text-brand-dark hover:underline mt-4"
          >
            {forgot ? "← Torna al login" : "Password dimenticata?"}
          </button>
        </div>
      </div>
    </div>
  );
}
